import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getProductionKPIs } from '@/lib/api';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Loader2, AlertTriangle, AlertCircle, Package, Tag, FlaskConical, Truck, CalendarDays, LayoutGrid, CheckCircle2, Clock, ShoppingCart, PackageX, GitBranch, AlertOctagon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const severityStyles = {
  alert: { card: 'border-l-4 border-l-red-600', value: 'text-red-700', badge: 'bg-red-900/80 text-white' },
  warn: { card: 'border-l-4 border-l-amber-500', value: 'text-amber-700', badge: 'bg-amber-600/80 text-white' },
  good: { card: 'border-l-4 border-l-emerald-500', value: 'text-emerald-700', badge: 'bg-emerald-600/80 text-white' },
  info: { card: 'border-l-4 border-l-blue-500', value: 'text-blue-700', badge: 'bg-blue-600/80 text-white' },
  neutral: { card: 'border-l-4 border-l-slate-400', value: 'text-slate-700', badge: 'bg-slate-600/80 text-white' },
};

const capacityStyles = {
  empty: { bg: 'bg-slate-100', text: 'text-slate-500', label: 'Empty', bar: 'bg-slate-300' },
  under_utilised: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Under-utilised', bar: 'bg-amber-400' },
  optimal: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Optimal', bar: 'bg-emerald-500' },
  getting_full: { bg: 'bg-orange-50', text: 'text-orange-700', label: 'Getting Full', bar: 'bg-orange-500' },
  overloaded: { bg: 'bg-red-50', text: 'text-red-700', label: 'Overloaded', bar: 'bg-red-600' },
  neutral: { bg: 'bg-slate-50', text: 'text-slate-500', label: 'Dispatched', bar: 'bg-slate-400' },
};

const insightIcons = {
  'alert-triangle': AlertTriangle,
  'alert-circle': AlertCircle,
  'package': Package,
  'tag': Tag,
  'flask-conical': FlaskConical,
  'git-branch': GitBranch,
  'alert-octagon': AlertOctagon,
};

const insightSeverityColors = {
  critical: 'text-red-600',
  warning: 'text-amber-600',
  info: 'text-blue-600',
};

const kpiIcons = {
  missing_dispatch_test: Truck,
  due_no_dispatch: CalendarDays,
  no_pattern: LayoutGrid,
  production_status: CheckCircle2,
  dispatch_slots: Clock,
  fabric_not_ordered: ShoppingCart,
  fabric_no_qty: PackageX,
};

const storeLabels = {
  store_perth: 'Perth',
  store_singapore: 'Singapore',
  store_internal: 'Internal',
};

function KPICard({ kpi, onClick }) {
  const style = severityStyles[kpi.severity] || severityStyles.neutral;
  const Icon = kpiIcons[kpi.id] || CheckCircle2;

  return (
    <div
      className={`relative bg-white rounded-lg shadow-sm border border-slate-200 p-5 ${style.card} overflow-hidden ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      data-testid={`kpi-${kpi.id}`}
      onClick={onClick}
    >
      <Icon className="absolute bottom-3 right-3 w-12 h-12 text-slate-200 opacity-60" strokeWidth={1} />
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">{kpi.label}</p>
      <p className="text-[11px] text-slate-400 mb-2">{kpi.sublabel}</p>
      <p className={`text-4xl font-bold mb-2 ${style.value}`}>{kpi.value}</p>
      <p className="text-xs text-slate-500 leading-relaxed mb-3 pr-10">{kpi.description}</p>
      <Badge className={`${style.badge} text-[10px] font-medium px-2 py-0.5`}>{kpi.badge}</Badge>
    </div>
  );
}

function DispatchSlotCard({ slot, onClick }) {
  const cap = capacityStyles[slot.capacity_status] || capacityStyles.empty;
  const barWidth = Math.min(100, (slot.garment_count / 65) * 100);

  // Build type summary string: "3 Jackets, 2 Waistcoats"
  const typeSummary = slot.garment_type_summary
    ? Object.entries(slot.garment_type_summary)
        .sort((a, b) => b[1] - a[1])
        .map(([type, count]) => `${count} ${type}${count > 1 ? 's' : ''}`)
        .join(', ')
    : '';

  return (
    <div
      className={`rounded-lg border overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${slot.is_past ? 'border-red-300 bg-red-50/30' : slot.is_anomaly ? 'border-amber-400 bg-amber-50/20' : 'border-slate-200 bg-white'}`}
      data-testid={`dispatch-${slot.date}`}
      onClick={onClick}
    >
      <div className={`px-4 py-3 ${slot.is_past ? 'bg-red-100/60' : slot.is_anomaly ? 'bg-amber-50' : 'bg-slate-50'} border-b ${slot.is_past ? 'border-red-200' : slot.is_anomaly ? 'border-amber-200' : 'border-slate-100'}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-sm font-bold ${slot.is_past ? 'text-red-700' : slot.is_anomaly ? 'text-amber-800' : 'text-slate-900'}`}>
              {slot.day_name} {slot.date}
            </p>
            <p className="text-[10px] text-slate-400 uppercase mt-0.5">{storeLabels[slot.store] || slot.store}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            {slot.is_past ? (
              <Badge className="bg-red-900/80 text-white text-[10px]">Past / Missed</Badge>
            ) : (
              <span className="text-xs text-slate-400">{slot.days_away}d away</span>
            )}
            {slot.is_anomaly && (
              <Badge className="bg-amber-500 text-white text-[9px] flex items-center gap-0.5 px-1.5">
                <AlertOctagon className="w-2.5 h-2.5" /> Non-standard day
              </Badge>
            )}
          </div>
        </div>
      </div>
      <div className="px-4 py-3 space-y-2">
        <div className="flex justify-between text-xs">
          <span className="text-slate-500">Garments planned</span>
          <span className="font-semibold text-slate-800">{slot.garment_count}</span>
        </div>
        {typeSummary && (
          <div className="text-[11px] text-slate-600 font-medium">{typeSummary}</div>
        )}
        <div className="flex justify-between text-xs">
          <span className="text-slate-500">Capacity</span>
          <Badge className={`${cap.bg} ${cap.text} border-0 text-[10px] font-medium`}>{cap.label}</Badge>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${cap.bar}`} style={{ width: `${barWidth}%` }} />
        </div>
        {slot.is_anomaly && slot.anomaly_reason && (
          <p className="text-[10px] text-amber-700 italic">{slot.anomaly_reason}</p>
        )}
        {slot.note && <p className="text-[11px] text-slate-400 italic pt-1">{slot.note}</p>}
      </div>
    </div>
  );
}

function InsightCard({ insight }) {
  const Icon = insightIcons[insight.icon] || AlertCircle;
  const color = insightSeverityColors[insight.severity] || 'text-slate-600';

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 flex gap-3" data-testid={`insight-${insight.id}`}>
      <div className={`flex-shrink-0 mt-0.5 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-sm font-semibold text-slate-800 mb-1">{insight.title}</p>
        <p className="text-xs text-slate-500 leading-relaxed">{insight.body}</p>
      </div>
    </div>
  );
}

export default function ProductionDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [store, setStore] = useState('all');
  const [windowDays, setWindowDays] = useState(10);
  const [stores, setStores] = useState([]);
  const [userStoreAccess, setUserStoreAccess] = useState(null); // null = loading, [] = unrestricted

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [kpiRes, storesRes] = await Promise.all([
        getProductionKPIs({ store: store !== 'all' ? store : undefined, window_days: windowDays }),
        stores.length ? Promise.resolve({ data: [] }) : api.get('/stores'),
      ]);
      setData(kpiRes.data);

      // Set user store access from backend response
      const cfg = kpiRes.data?.config || {};
      if (userStoreAccess === null) {
        const accessible = cfg.user_accessible_stores || [];
        setUserStoreAccess(accessible);
        // Auto-select first store if user is restricted to stores
        if (!cfg.is_unrestricted && accessible.length > 0 && store === 'all') {
          setStore(accessible.length === 1 ? accessible[0] : 'all');
        }
      }

      if (!stores.length && storesRes.data) {
        const storeList = Array.isArray(storesRes.data) ? storesRes.data : storesRes.data.stores || [];
        setStores(storeList);
      }
    } catch (err) {
      toast.error('Failed to load production dashboard');
    } finally {
      setLoading(false);
    }
  }, [store, windowDays, stores.length, userStoreAccess]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const { kpis = [], dispatch_slots = [], insights = [], config = {} } = data || {};

  // Group dispatch slots by store
  const slotsByStore = {};
  dispatch_slots.forEach(s => {
    const key = s.store;
    if (!slotsByStore[key]) slotsByStore[key] = [];
    slotsByStore[key].push(s);
  });

  // Build click-through URL params per KPI
  const getKpiClickHandler = (kpiId) => {
    const storeParam = store !== 'all' ? `&store=${store}` : '';
    const routes = {
      missing_dispatch_test: `/garments?no_dispatch=1${storeParam}`,
      due_no_dispatch: `/garments?no_dispatch=1${storeParam}`,
      no_pattern: `/garments?no_pattern=1${storeParam}`,
      production_status: `/garments?${storeParam.replace('&', '')}`,
      dispatch_slots: null,
      fabric_not_ordered: `/fabric-orders?status=not_ordered`,
      fabric_no_qty: `/fabric-orders?missing_qty=1`,
    };
    const route = routes[kpiId];
    return route ? () => navigate(route) : undefined;
  };

  // Click-through for dispatch slot cards
  const handleDispatchSlotClick = (slot) => {
    const storeParam = slot.store ? `&store=${slot.store}` : '';
    navigate(`/garments?planned_dispatch_date=${slot.date}${storeParam}`);
  };

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto space-y-8" data-testid="production-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Production Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">
            {config.total_garments} garments &middot; {config.window_days}-day window: {config.window_start} &ndash; {config.window_end}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500 whitespace-nowrap">Window</label>
            <Input
              type="number"
              value={windowDays}
              onChange={e => setWindowDays(Math.max(1, Math.min(90, parseInt(e.target.value) || 14)))}
              className="w-16 h-8 text-xs"
              min={1}
              max={90}
              data-testid="window-days-input"
            />
            <span className="text-xs text-slate-400">days</span>
          </div>
          <Select value={store} onValueChange={setStore}>
            <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="store-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(config.is_unrestricted || (userStoreAccess && userStoreAccess.length > 1)) && (
                <SelectItem value="all">All Stores</SelectItem>
              )}
              {stores.filter(s => {
                if (!s.active) return false;
                if (config.is_unrestricted) return true;
                return userStoreAccess && userStoreAccess.includes(s.store_id);
              }).map(s => (
                <SelectItem key={s.store_id} value={s.store_id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Cards */}
      <section>
        <h2 className="text-[11px] font-semibold uppercase tracking-[2px] text-slate-400 mb-4">Key Production Indicators</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {kpis.map(kpi => <KPICard key={kpi.id} kpi={kpi} onClick={getKpiClickHandler(kpi.id)} />)}
        </div>
      </section>

      {/* Dispatch Day Load */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[11px] font-semibold uppercase tracking-[2px] text-slate-400">Dispatch Day Load</h2>
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { label: '<40 Under-utilised', cls: 'bg-amber-100 text-amber-700' },
              { label: '40-55 Optimal', cls: 'bg-emerald-100 text-emerald-700' },
              { label: '55-65 Getting full', cls: 'bg-orange-100 text-orange-700' },
              { label: '>65 Overloaded', cls: 'bg-red-100 text-red-700' },
            ].map(c => (
              <span key={c.label} className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${c.cls}`}>{c.label}</span>
            ))}
          </div>
        </div>

        {Object.entries(slotsByStore).map(([storeId, slots]) => (
          <div key={storeId} className="mb-6">
            <h3 className="text-xs font-semibold text-slate-600 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-orange-400" />
              {storeLabels[storeId] || storeId}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {slots.map(slot => <DispatchSlotCard key={`${slot.date}-${slot.store}`} slot={slot} onClick={() => handleDispatchSlotClick(slot)} />)}
            </div>
          </div>
        ))}
      </section>

      {/* Insights */}
      {insights.length > 0 && (
        <section>
          <h2 className="text-[11px] font-semibold uppercase tracking-[2px] text-slate-400 mb-4">Additional Insights</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {insights.map(insight => <InsightCard key={insight.id} insight={insight} />)}
          </div>
        </section>
      )}
    </div>
  );
}
